// owner/web/browserAlerts.js — the WEB's alerts channel for the owner pages (owner/alerts/deviceAlerts.js).
//
// A computer gets browser alerts through the same web-push helpers and server routes the staff panel uses
// (utils/serviceWorker.js, services/notificationApi.js) — nothing new on the server. A phone's browser gets none:
// the store app is offered instead, once a store link exists (Adil, 5 Nov 2026); the web and the home-screen web
// app keep working as today for everything else.
// An owner's alerts follow the sign-in, as on the phone (§10 step 7): signing out removes this browser first, so a
// shared computer stops getting the owner's alerts (8d audit).
//
// ⚠️ Web only. utils/serviceWorker.js keeps the device id in browser storage, which the store app must never use
// (§10 step 8 decision 2), so this file is installed by the web entry (src/index.js) and never imported by owner
// pages. The app bundle check refuses owner/web/ and utils/serviceWorker.js (scripts/check-owner-app-bundle.js).
// Plan: tasks/owner-mobile-app-build.md §10 "8d".

import { notificationApi } from '../../services/notificationApi';
import {
  pushSupported,
  permissionState,
  isIos,
  subscribeThisBrowser,
  unsubscribeThisBrowser,
  getStoredDeviceId,
  setStoredDeviceId
} from '../../utils/serviceWorker';
import { createStateStore } from '../alerts/deviceAlerts';

const isAndroid = () => typeof navigator !== 'undefined' && /Android/i.test(navigator.userAgent || '');

/** Which phone this browser runs on, for the store link; null on a computer. */
export const phonePlatform = () => {
  if (isIos()) return 'ios';
  if (isAndroid()) return 'android';
  return null;
};

export const createBrowserAlerts = () => {
  const storePlatform = phonePlatform();
  const kind = storePlatform ? 'phoneBrowser' : 'browser';
  const store = createStateStore({
    status: kind === 'browser' && pushSupported() ? 'checking' : 'unsupported',
    storePlatform
  });
  let vapidPublicKey = null;

  const refresh = async () => {
    if (kind !== 'browser' || !pushSupported()) return;
    try {
      const config = await notificationApi.config();
      vapidPublicKey = config.vapidPublicKey || null;
      if (!config.enabled || !vapidPublicKey) return store.set({ status: 'unavailable' });
      if (permissionState() === 'denied') return store.set({ status: 'denied' });

      // "On" only when the server still holds THIS browser's row and its key is current; anything else needs the
      // owner to turn it on (again), which is idempotent on the server.
      const deviceId = getStoredDeviceId();
      const { devices = [] } = deviceId ? await notificationApi.me() : {};
      const mine = devices.find((d) => String(d._id) === String(deviceId));
      const on = permissionState() === 'granted' && mine && mine.health === 'ok';
      return store.set({ status: on ? 'on' : 'off' });
    } catch (error) {
      // Could not read — say so with a retry, never "unavailable" or "off" (OWNER_EXPERIENCE_MAP: no fake states).
      return store.set({ status: 'error' });
    }
  };

  /** → { ok } | { ok: false, reason } (utils/serviceWorker's machine reasons) | { ok: false, error } (a server refusal). */
  const enable = async () => {
    if (kind !== 'browser' || !vapidPublicKey) return { ok: false, reason: 'not_configured' };
    try {
      const result = await subscribeThisBrowser(vapidPublicKey);
      if (!result.ok) {
        if (result.reason === 'denied') store.set({ status: 'denied' });
        return result;
      }
      const saved = await notificationApi.subscribe(result.subscription);
      setStoredDeviceId(saved.deviceId);
      store.set({ status: 'on' });
      return { ok: true };
    } catch (error) {
      return error?.response ? { ok: false, error } : { ok: false, reason: 'subscribe_failed' };
    }
  };

  /** This browser stops getting alerts: the server row, the browser's half and the stored id. */
  const disable = async () => {
    const deviceId = getStoredDeviceId();
    // A row already gone (removed elsewhere) is the outcome wanted; the browser's half is dropped either way.
    if (deviceId) await notificationApi.removeDevice(deviceId).catch(() => {});
    await unsubscribeThisBrowser();
    setStoredDeviceId(null);
    store.set({ status: 'off' });
  };

  /** Before an owner's sign-out, while the pass is still valid (owner/shell/useOwnerSignOut.js). */
  const beforeSignOut = async () => {
    if (kind === 'browser' && getStoredDeviceId()) await disable();
  };

  return { kind, getState: store.get, subscribe: store.subscribe, refresh, enable, disable, beforeSignOut };
};
